package landing.ui

import androidx.compose.foundation.ExperimentalFoundationApi
import androidx.compose.foundation.Image
import androidx.compose.foundation.background
import androidx.compose.foundation.layout.*
import androidx.compose.foundation.pager.HorizontalPager
import androidx.compose.foundation.pager.rememberPagerState
import androidx.compose.material3.MaterialTheme
import androidx.compose.material3.Scaffold
import androidx.compose.material3.Text
import androidx.compose.runtime.*
import androidx.compose.ui.Alignment
import androidx.compose.ui.Modifier
import androidx.compose.ui.graphics.BlendMode
import androidx.compose.ui.graphics.Brush
import androidx.compose.ui.graphics.Color
import androidx.compose.ui.layout.ContentScale
import androidx.compose.ui.layout.onGloballyPositioned
import androidx.compose.ui.layout.positionInWindow
import androidx.compose.ui.text.font.FontWeight
import androidx.compose.ui.text.style.TextAlign
import androidx.compose.ui.unit.dp
import com.osg.truematch.ui.spacing
import com.osg.truematch.utilities.extensions.pxToDp
import common.ds.Place
import common.ui.paint.GlassView
import common.ui.pyrotechnics.pager.SlidingIndicator
import landing.ds.ResourceSlogan
import landing.model.state.SloganPagerState
import loadResourceImage
import loadResourceString
import truematchmp.composeapp.generated.resources.Res
import truematchmp.composeapp.generated.resources.logo_no_background_glow


@OptIn(ExperimentalFoundationApi::class)
@Composable
fun SloganPager(
    sloganPagerState: SloganPagerState,
    onReachEnd: () -> Unit,
    modifier: Modifier = Modifier
) {
    val pagerState = rememberPagerState(
        initialPage = sloganPagerState.initialPage,
        pageCount = {
            ResourceSlogan.resourceSlogans.size
        })

    var isReachEnd by remember {
        mutableStateOf(false)
    }

    if ((ResourceSlogan.resourceSlogans.size - 1) == pagerState.currentPage) {
        isReachEnd = true
    }

    LaunchedEffect(isReachEnd) {
        if (isReachEnd) {
            onReachEnd()
        }
    }

    LaunchedEffect(sloganPagerState.nextPage) {
        if (sloganPagerState.nextPage > -1 && pagerState.currentPage != sloganPagerState.nextPage) {
            pagerState.animateScrollToPage(sloganPagerState.nextPage)
        }
    }

    var bottomPanelHeightPx by remember {
        mutableIntStateOf(0)
    }

    Scaffold(
        modifier = modifier,
        contentWindowInsets = WindowInsets(0, 0, 0, 0),
    ) { innerPadding ->
        Box(
            modifier = Modifier
                .padding(innerPadding)
        ) {
            HorizontalPager(
                state = pagerState,
                beyondBoundsPageCount = ResourceSlogan.resourceSlogans.size,
            ) { page ->
                SloganCard(
                    sloganContent = ResourceSlogan.resourceSlogans[page],
                    sloganTextBottomPaddingPx = bottomPanelHeightPx
                )
            }
            Box(
                modifier = Modifier
                    .align(Alignment.BottomCenter)
                    .navigationBarsPadding()
                    .onGloballyPositioned {
                        bottomPanelHeightPx = it.size.height
                    },
            ) {
                SlidingIndicator(
                    pagerState = pagerState,
                    modifier = Modifier
                        .fillMaxWidth()
                        .padding(MaterialTheme.spacing.medium.dp)
                        .heightIn(max = 100.dp)
                        .align(Alignment.Center)
                )
            }
        }
    }

}

@Composable
fun SloganCard(
    sloganContent: ResourceSlogan,
    sloganTextBottomPaddingPx: Int
) {
    val sloganTextBottomPadding = sloganTextBottomPaddingPx.pxToDp()
    val childMeasures = List(1) { Place() }
    val gradient = Brush.verticalGradient(
        colorStops = arrayOf(
            .45f to Color.Transparent,
            .7f to Color.Black.copy(alpha = .6f),
            1.0f to Color.Black.copy(alpha = .4f),
        )
    )
    Box(
        Modifier
            .fillMaxSize(),
        contentAlignment = Alignment.BottomCenter
    ) {
        GlassView(
            imageBitmap = loadResourceImage(sloganContent.image),
            childMeasures = childMeasures,
            blendMode = BlendMode.Clear,
            childCornerRadius = 0,
            strokeSize = 0.dp,
            blurX = 3.dp,
            blurY = 3.dp
        )
        Spacer(
            modifier = Modifier
                .fillMaxSize()
                .background(gradient)
        )
        Column(
            modifier = Modifier
                .fillMaxSize(),
            horizontalAlignment = Alignment.CenterHorizontally,
            verticalArrangement = Arrangement.SpaceBetween
        ) {
            Image(
                modifier = Modifier
                    .weight(1f, fill = true)
                    .padding(top = 16.dp)
                    .statusBarsPadding()
                    .onGloballyPositioned {
                        childMeasures[0].apply {
                            sizeX = it.size.width
                            sizeY = it.size.height
                            offsetX = it.positionInWindow().x
                            offsetY = it.positionInWindow().y
                        }
                    },
                painter = loadResourceImage(Res.drawable.logo_no_background_glow),
                contentDescription = "logo",
                contentScale = ContentScale.FillHeight
            )
            SloganText(
                modifier = Modifier
                    .weight(6f, fill = false)
                    .padding(bottom = sloganTextBottomPadding)
                    .navigationBarsPadding(),
                title = loadResourceString(sloganContent.title),
                description = loadResourceString(sloganContent.description)
            )
        }
    }
}

@Composable
fun SloganText(
    title: String,
    description: String,
    modifier: Modifier = Modifier
) {
    Column(
        modifier = modifier
            .fillMaxWidth()
            .padding(8.dp),
        horizontalAlignment = Alignment.CenterHorizontally,
        verticalArrangement = Arrangement.Bottom
    ) {
        Text(
            text = title,
            fontWeight = FontWeight.Bold,
            style = MaterialTheme.typography.headlineSmall,
            textAlign = TextAlign.Center,
            color = Color.White
        )
        Spacer(Modifier.size(8.dp))
        Text(
            text = description,
            style = MaterialTheme.typography.bodyLarge,
            color = Color.White,
            textAlign = TextAlign.Center,
        )
    }
}